import { useCallback, useEffect, useState } from 'react';
import { MakeTag } from '../lib/jobMakes.tsx';
import { api, fmt, fmtDate, num, poNo, type User } from '../api.ts';
import DataTable, { type Column } from '../components/DataTable.tsx';
import UndoDialog from '../components/UndoDialog.tsx';
import { useList } from '../lib/useList.ts';

type Tab = 'queue' | 'log';

interface QueueRow {
  job_id: number;
  job_number: string;
  customer_name: string;
  box_name: string;
  po_number: string | null;
  ordered_quantity: string;
  dispatched_quantity: string;
  pending_dispatch: string;
  delivery_date: string | null;
  priority: string;
  delivery_state: string;
  production_done: boolean;
  last_stage: string | null;
}

interface LogRow {
  dispatch_id: number;
  job_number: string;
  customer_name: string;
  box_name: string;
  po_number: string | null;
  quantity: string;
  dispatch_date: string;
  status: 'PENDING' | 'DISPATCHED' | 'DELIVERED' | 'CANCELLED';
  delivery_date: string | null;
  line_value: string | null;
  notes: string | null;
}

const today = () => new Date().toISOString().slice(0, 10);

export default function Dispatch({
  user, view, prefillSearch,
}: {
  user: User; view: Tab;
  /** A job number to land pre-searched on, from the Production hand-off. */
  prefillSearch?: string;
}) {
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const canCancel = user.role === 'ADMIN' || user.role === 'MANAGER';

  // The sidebar owns which view is showing; clear transient messages on change.
  useEffect(() => { setError(''); setNotice(''); }, [view]);

  return (
    <div className="panel">
      {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
      {notice && <div style={{ margin: 16 }} className="tag ready notice">{notice}</div>}

      {view === 'queue'
        ? <QueueTab setError={setError} setNotice={setNotice} prefillSearch={prefillSearch}
                    canUndo={user.permissions.includes('production.record')} />
        : <LogTab canCancel={canCancel} setError={setError} setNotice={setNotice} />}
    </div>
  );
}

type Setters = { setError: (s: string) => void; setNotice: (s: string) => void };

function QueueTab({ setError, setNotice, prefillSearch, canUndo }: Setters & { prefillSearch?: string; canUndo: boolean }) {
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [search, setSearch] = useState(prefillSearch ?? '');
  const [readyOnly, setReadyOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState<QueueRow | null>(null);
  const [qty, setQty] = useState('');
  const [date, setDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [menuFor, setMenuFor] = useState<{ row: QueueRow; right: number; top?: number; bottom?: number } | null>(null);
  const [undoJob, setUndoJob] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      if (search.trim()) qs.set('search', search.trim());
      if (readyOnly) qs.set('readyOnly', 'true');
      setRows((await api.get<{ items: QueueRow[] }>(
        `/api/dispatch/queue${qs.toString() ? `?${qs}` : ''}`)).items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the dispatch queue');
    } finally {
      setLoading(false);
    }
  }, [search, readyOnly, setError]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  function open(r: QueueRow) {
    setTarget(r);
    setQty(String(num(r.pending_dispatch)));
    setDate(today());
    setNotes('');
    setNotice('');
  }

  async function send() {
    if (!target) return;
    setBusy(true); setError('');
    try {
      const res = await api.post<{
        jobNumber: string; pendingDispatch: number; fullyDispatched: boolean;
      }>(`/api/dispatch/jobs/${target.job_id}`, {
        quantity: Number(qty),
        dispatchDate: date,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      setNotice(
        res.fullyDispatched
          ? `${res.jobNumber} fully dispatched`
          : `${fmt(qty)} sent on ${res.jobNumber} · ${fmt(res.pendingDispatch)} still owed`,
      );
      setTarget(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the dispatch');
    } finally {
      setBusy(false);
    }
  }

  const pending = target ? num(target.pending_dispatch) : 0;
  const problem = !qty || Number(qty) <= 0
    ? 'Enter a quantity'
    : Number(qty) > pending
      ? `Only ${fmt(pending)} left to dispatch`
      : '';

  return (
    <>
      <div className="stagebar">
        <input className="search" placeholder="Search company, box, job or PO"
               value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="hint" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={readyOnly}
                 onChange={(e) => setReadyOnly(e.target.checked)} />
          Production finished only
        </label>
        <span className="hint" style={{ alignSelf: 'center', marginLeft: 'auto' }}>
          Quantities are what the customer ordered — the extra run covers waste and is not sold
        </span>
      </div>

      {!loading && rows.length === 0 ? (
        <div className="empty">
          Nothing waiting to go out{readyOnly ? ' with production finished' : ''}.
        </div>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Job</th><th>Company</th><th>Box</th>
                <th className="num">Ordered</th><th className="num">Sent</th>
                <th className="num">Owed</th><th>Due</th><th>State</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.job_id}
                    className={r.delivery_state === 'LATE' ? 'late'
                             : r.delivery_state === 'DUE_SOON' ? 'soon' : ''}>
                  <td>{r.job_number}<MakeTag number={r.job_number} /></td>
                  <td>{r.customer_name}</td>
                  <td>{r.box_name}</td>
                  <td className="num">{fmt(r.ordered_quantity)}</td>
                  <td className="num dim">{fmt(r.dispatched_quantity)}</td>
                  <td className="num"><strong>{fmt(r.pending_dispatch)}</strong></td>
                  <td className="dim">{fmtDate(r.delivery_date)}</td>
                  <td>
                    {r.production_done
                      ? <span className="tag ready">Ready</span>
                      : <span className="tag blocked">In production</span>}
                  </td>
                  <td>
                    <button className="btn primary" disabled={!r.production_done}
                            title={r.production_done ? '' : 'Production is not finished'}
                            onClick={() => open(r)}>
                      Dispatch
                    </button>
                    {canUndo && r.production_done && r.last_stage && (
                      <button className="btn" style={{ marginLeft: 6 }} aria-haspopup="menu"
                              aria-label={`More actions for ${r.job_number}`}
                              onClick={(e) => {
                                const b = e.currentTarget.getBoundingClientRect();
                                const right = window.innerWidth - b.right;
                                setMenuFor(window.innerHeight - b.bottom < 120
                                  ? { row: r, right, bottom: window.innerHeight - b.top + 6 }
                                  : { row: r, right, top: b.bottom + 6 });
                              }}>
                        ⋯
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {menuFor && (
        <>
          <div className="menu-scrim" onClick={() => setMenuFor(null)} />
          <div className="menu" role="menu"
               style={{ position: 'fixed', top: menuFor.top, bottom: menuFor.bottom, right: menuFor.right, left: 'auto' }}>
            <button role="menuitem" onClick={() => { setNotice(''); setUndoJob(menuFor.row.job_id); setMenuFor(null); }}>
              Back to {menuFor.row.last_stage}…
            </button>
          </div>
        </>
      )}

      {undoJob !== null && (
        <UndoDialog previewUrl={`/api/production/jobs/${undoJob}/undo`}
                    onClose={() => setUndoJob(null)}
                    onDone={(msg) => { setNotice(msg); void load(); }} />
      )}

      {target && (
        <div className="backdrop" onClick={() => setTarget(null)}>
          <div className="modal" style={{ width: 500 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Dispatch — {target.job_number}</h3>
              <p>
                {target.customer_name} · {target.box_name} · {poNo(target.po_number)} ·{' '}
                {fmt(target.pending_dispatch)} of {fmt(target.ordered_quantity)} still owed
              </p>
            </div>
            <div className="modal-body">
              <div className="row2">
                <div className="field">
                  <label htmlFor="dq" className="req">Quantity going out</label>
                  <input id="dq" type="number" min="1" max={pending} value={qty} autoFocus
                         onChange={(e) => setQty(e.target.value)} />
                  <div className="hint">
                    {Number(qty) > 0 && Number(qty) <= pending
                      ? `${fmt(pending - Number(qty))} will still be owed`
                      : 'A job can go out in several loads'}
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="dd2">Dispatch date</label>
                  <input id="dd2" type="date" value={date}
                         onChange={(e) => setDate(e.target.value)} />
                </div>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="dn">Notes</label>
                <input id="dn" type="text" placeholder="Vehicle, LR number, driver"
                       value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
            <div className="modal-foot">
              {problem && <span className="hint" style={{ marginRight: 'auto' }}>{problem}</span>}
              <button className="btn" onClick={() => setTarget(null)} disabled={busy}>Cancel</button>
              <button className="btn primary" onClick={send} disabled={busy || !!problem}>
                {busy ? 'Saving…' : 'Record dispatch'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const STATUS_TAG: Record<string, string> = {
  PENDING: 'blocked', DISPATCHED: 'high', DELIVERED: 'ready', CANCELLED: 'blocked',
};
const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending', DISPATCHED: 'Dispatched', DELIVERED: 'Delivered', CANCELLED: 'Cancelled',
};

function LogTab({ canCancel, setError, setNotice }: Setters & { canCancel: boolean }) {
  const list = useList<LogRow>('/api/dispatch/log', 'dispatches',
                               { sort: 'dispatch_date', dir: 'desc' });
  const load = list.reload;
  const [busy, setBusy] = useState<number | null>(null);
  const [undoId, setUndoId] = useState<number | null>(null);

  async function act(r: LogRow, what: 'deliver' | 'cancel') {
    if (what === 'cancel') {
      const reason = window.prompt(`Why is this dispatch being cancelled?`);
      if (reason === null) return;
      setBusy(r.dispatch_id); setError('');
      try {
        const res = await api.post<{ pendingDispatch: number }>(
          `/api/dispatch/${r.dispatch_id}/cancel`, { reason });
        setNotice(`Cancelled · ${fmt(res.pendingDispatch)} back to dispatch on ${r.job_number}`);
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not cancel');
      } finally { setBusy(null); }
      return;
    }

    setBusy(r.dispatch_id); setError('');
    try {
      await api.post(`/api/dispatch/${r.dispatch_id}/deliver`, { deliveryDate: today() });
      setNotice(`${r.job_number} marked delivered`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not mark delivered');
    } finally { setBusy(null); }
  }

  const columns: Column<LogRow>[] = [
    { key: 'dispatch_date', label: 'Sent', type: 'date',
      render: (r) => fmtDate(r.dispatch_date) },
    { key: 'job_number', label: 'Job',
      render: (r) => <><span className="mono">{r.job_number}</span><MakeTag number={r.job_number} /></> },
    { key: 'customer_name', label: 'Company' },
    { key: 'box_name', label: 'Box' },
    { key: 'quantity', label: 'Quantity', type: 'number', align: 'right',
      render: (r) => fmt(r.quantity) },
    { key: 'line_value', label: 'Value', type: 'number', align: 'right', secondary: true,
      render: (r) => (
        <span className="dim">
          {r.line_value && Number(r.line_value) > 0 ? `₹${fmt(r.line_value)}` : '—'}
        </span>
      ) },
    { key: 'delivery_date', label: 'Delivered', type: 'date', secondary: true,
      render: (r) => <span className="dim">{fmtDate(r.delivery_date)}</span> },
    { key: 'status', label: 'Status', type: 'choice',
      choices: Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })),
      render: (r) => (
        <span className={`tag ${STATUS_TAG[r.status]}`}>{STATUS_LABEL[r.status]}</span>
      ) },
    {
      key: 'act', label: '', sortable: false, filterable: false,
      render: (r) => (r.status === 'DISPATCHED' ? (
        <>
          <button className="btn" disabled={busy === r.dispatch_id}
                  onClick={() => act(r, 'deliver')}>Delivered</button>
          {canCancel && (
            <button className="btn" style={{ marginLeft: 6 }}
                    disabled={busy === r.dispatch_id}
                    onClick={() => act(r, 'cancel')}>Cancel</button>
          )}
        </>
      ) : r.status === 'DELIVERED' ? (
        <button className="btn" onClick={() => setUndoId(r.dispatch_id)}>Undo</button>
      ) : null),
    },
  ];

  return (
    <>
      <DataTable
        list={list}
        columns={columns}
        exportName="/api/dispatch/log"
        rowKey={(r) => r.dispatch_id}
        /* A cancelled shipment stays on the log, struck through rather than
           removed: it happened, and the quantity went back because of it. */
        rowClass={(r) => (r.status === 'CANCELLED' ? 'struck' : '')}
        empty="No shipments recorded."
      />
      {undoId !== null && (
        <UndoDialog previewUrl={`/api/dispatch/${undoId}/undo-delivered`}
                    onClose={() => setUndoId(null)}
                    onDone={(msg) => { setNotice(msg); void load(); }} />
      )}
    </>
  );
}
